import { Account } from '../types';
import isNil from 'lodash/isNil';
import some from 'lodash/some';
import {
	IAccount,
	IAccountQuery,
	IAccountsQuery,
	IUser,
	useAccountQuery,
	useAccountsQuery,
	useNumAccountsQuery,
} from '@copilot/data/graphql/_generated';
import { useSelector } from 'react-redux';
import { RootState } from '@copilot/common/store/reducers';
import { QueryResponseType } from '@copilot/common/hooks/types';
import { ApolloQueryResult } from '@apollo/client';
import { AccountDetail, UserDetail } from '@copilot/common/pages/accountDetailsPage/types';

type AccountDTO = ArrayElement<NonNullable<IAccountsQuery['admin_accounts']>>;

/**
 * A tuple for the accounts query and a refetch query
 */
type AccountsHookType = [
	QueryResponseType<{
		accounts: Account[];
		activeUserCount: number;
		totalUserCount: number;
	}>,
	QueryAccountsCallbackType
];

/**
 * A tuple for the account query and a refetch query
 */
type AccountHookType = [
	QueryResponseType<{
		account: AccountDetail | undefined;
	}>,
	QueryAccountCallbackType
];

/**
 * Type of callback function for refetching account query
 */
type QueryAccountCallbackType = () => Promise<ApolloQueryResult<IAccountQuery>>;

/**
 * Type of callback function for refetching accounts query
 */
export type QueryAccountsCallbackType = () => Promise<ApolloQueryResult<IAccountsQuery>>;

/**
 * Convert the account dto into an Account model
 * @param accountDTO
 * @returns
 */
export function toAccount(accountDTO: AccountDTO): Account {
	if (isNil(accountDTO.orgInfo))
		throw new Error('All accounts must have an organization info property.');
	const { activeUsers, inactiveUsers, linkedInConnection } = getUserCounts(accountDTO.users);

	const primaryContact = accountDTO.primaryContact;

	return {
		id: accountDTO.id,
		name: accountDTO.name,
		activeUsers,
		inactiveUsers,
		linkedInConnection,
		status: accountDTO.isActive ? 'Active' : 'Deactivated',
		accountManager: accountDTO.accountManager ?? undefined,
		primaryContact: isNil(primaryContact) ? undefined : { id: primaryContact.id },
		orgId: accountDTO.orgInfo?.id,
	};
}

type AccountDetailDTO = NonNullable<IAccountQuery['admin_account']>;
type AccountDetailUserDTO = ArrayElement<NonNullable<AccountDetailDTO['users']>>;

/**
 * Convert the account dto into an Account Details model
 * @param accountDTO
 * @returns
 */
export function toAccountDetail(accountDTO: AccountDetailDTO): AccountDetail {
	if (accountDTO.type !== 'REGULAR') throw new Error('Account should be regular child account');
	if (isNil(accountDTO.orgInfo))
		throw new Error('All accounts must have an organization info property.');
	const { activeUsers } = getUserCounts(accountDTO.users);
	const primaryContact = accountDTO.primaryContact;

	return {
		id: accountDTO.id,
		name: accountDTO.name,
		activeUsers,
		status: accountDTO.isActive ? 'Active' : 'Deactivated',
		primaryContact: isNil(primaryContact)
			? undefined
			: {
					id: primaryContact.id,
			  },
		users: accountDTO.users.map((user) =>
			toUserDetail(user, accountDTO.primaryContact?.id === user.id)
		),
		orgId: accountDTO.orgInfo.id,
	};
}

function toUserDetail(userDto: AccountDetailUserDTO, isPrimaryContact: boolean): UserDetail {
	return {
		firstName: userDto.firstName,
		id: userDto.id,
		accountId: userDto.accountId,
		isActive: userDto.isActive,
		lastName: userDto.lastName,
		lastSeen: userDto.lastSeen,
		userName: userDto.userName,
		isPrimaryContact,
		isLinkedInLoggedOut: getIsUserLinkedInLoggedOut(userDto),
		role: userDto.orgMemberInfo?.role ?? undefined,
		vpnPort: userDto.vpnPort ?? undefined,
	};
}

/**
 * Get the counts for a given list of users
 * @param users
 */
function getUserCounts(
	users: IAccount['users']
): Pick<Account, 'activeUsers' | 'inactiveUsers' | 'linkedInConnection'> {
	return users.reduce(
		(acc, user) => {
			if (user.isActive) {
				acc.activeUsers++;
			} else {
				acc.inactiveUsers++;
			}

			const isLoggedOut = getIsUserLinkedInLoggedOut(user);
			if (isLoggedOut) {
				acc.linkedInConnection++;
			}
			return acc;
		},
		{ activeUsers: 0, inactiveUsers: 0, linkedInConnection: 0 }
	);
}

function getIsUserLinkedInLoggedOut(user: IUser): boolean {
	return some(user.issues, (issue) => issue.type === 'LOGGED_OUT_OF_LINKEDIN');
}

/**
 * Get the user counts for a given list of accounts
 */
function getTotalUserCounts(accounts: Account[]): {
	activeUserCount: number;
	inactiveUserCount: number;
} {
	return accounts.reduce(
		(obj, account) => {
			return {
				activeUserCount: obj.activeUserCount + account.activeUsers,
				inactiveUserCount: obj.inactiveUserCount + account.inactiveUsers,
			};
		},
		{
			activeUserCount: 0,
			inactiveUserCount: 0,
		}
	);
}

/**
 * Get child accounts for an account
 * @returns
 */
export function useAccounts(): AccountsHookType {
	const currentUserTenantId = useSelector((state: RootState) => state.app.tenant);
	const filter = {
		accountsFilter: {
			masterAccountId: currentUserTenantId,
		},
	};
	const { data, error, loading, refetch } = useAccountsQuery({
		variables: filter,
		notifyOnNetworkStatusChange: true,
	});
	const refetchAccounts = () => refetch(filter);

	if (loading === true) return [{ data: undefined, error: undefined, loading }, refetchAccounts];
	if (!isNil(error)) return [{ data: undefined, error, loading }, refetchAccounts];

	// Filter out accounts that don't have a related organization
	const accounts = data?.admin_accounts.filter((acc) => !isNil(acc.orgInfo)).map(toAccount) ?? [];
	const userCounts = getTotalUserCounts(accounts);
	return [
		{
			data: {
				accounts,
				activeUserCount: userCounts.activeUserCount,
				totalUserCount: userCounts.activeUserCount + userCounts.inactiveUserCount,
			},
			error: undefined,
			loading: false,
		},
		refetchAccounts,
	];
}

/**
 * Gets the current account details
 * User the app state to determine what tenant
 */
export function useCurrentAccount() {
	const adminAccountId = useSelector((state: RootState) => state.app.tenant);
	return isNil(adminAccountId)
		? { data: undefined, loading: true }
		: useAccountQuery({
				variables: {
					adminAccountId,
				},
		  });
}

/**
 * Get an account by id
 * @param id
 */
export function useAccount(id: string): AccountHookType {
	const { data, error, loading, refetch } = useAccountQuery({
		variables: {
			adminAccountId: id,
		},
	});

	if (loading) return [{ data: undefined, error: undefined, loading }, refetch];
	if (!isNil(error)) return [{ data: undefined, error, loading }, refetch];

	const account = isNil(data) ? undefined : toAccountDetail(data.admin_account);

	return [
		{
			data: {
				account: account,
			},
			loading: false,
			error: undefined,
		},
		refetch,
	];
}

/**
 * Get the count of child accounts
 */
export function useNumAccounts(): QueryResponseType<number> {
	const currentUserTenantId = useSelector((state: RootState) => state.app.tenant);
	const { data, error, loading } = useNumAccountsQuery({
		variables: {
			accountsFilter: {
				masterAccountId: currentUserTenantId,
			},
		},
	});

	if (loading) return { data: undefined, error: undefined, loading };
	if (!isNil(error)) return { data: undefined, error, loading: false };
	return { data: data?.admin_num_accounts ?? 0, error: undefined, loading: false };
}
